import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Check, ShieldCheck } from 'lucide-react'
import { toast } from 'sonner'
import { ApiV2Error } from '@/lib/api-v2-client'
import { startCheckout } from '@/lib/checkout'
import { MAX_ANNUAL_SAVING_PERCENT, tiers, type BillingInterval, type PaidPlan } from '@/lib/plans'
import { useAuth } from '@/hooks/use-auth'
import { PromoField } from '@/components/promo-field'
import { usePromoCode } from '@/hooks/use-promo-code'
import { classifyPromoError } from '@/lib/promo'
import { canManageBilling, isAlreadySubscribed } from '@/lib/subscription-access'

// Render integer prices bare ($3) and fractional ones to 2 decimals ($2.50).
const formatPrice = (n: number) => (Number.isInteger(n) ? `${n}` : n.toFixed(2))

type PlanPickerProps = {
  selectedPlan?: PaidPlan
  defaultInterval?: BillingInterval
}

/** In-app plan choice: each card opens the Paddle overlay directly. */
export function PlanPicker({ selectedPlan = 'pro', defaultInterval = 'annual' }: PlanPickerProps) {
  const [interval, setInterval] = useState<BillingInterval>(defaultInterval)
  const [loadingPlan, setLoadingPlan] = useState<PaidPlan | null>(null)
  const { user } = useAuth()
  const isAnnual = interval === 'annual'
  const promo = usePromoCode(selectedPlan, interval)

  async function choose(plan: PaidPlan) {
    setLoadingPlan(plan)
    try {
      // With a code in the field, checkout starts only after the server says the code is valid for this plan.
      const picked = await promo.choose(plan)
      if (!picked.ok) return
      await startCheckout(plan, interval, { promoCode: picked.code })
    } catch (err) {
      if (classifyPromoError(err).kind === 'invalid') {
        promo.markInvalid()
        return
      }
      if (isAlreadySubscribed(err)) {
        window.location.assign('/dashboard/settings?tab=billing')
        return
      }
      toast.error(err instanceof ApiV2Error ? err.message : 'Failed to start checkout')
    } finally {
      setLoadingPlan(null)
    }
  }

  if (!canManageBilling(user)) {
    return (
      <p className="rounded-lg border p-6 text-muted-foreground">
        Ask your organization owner or an admin to choose a plan. Only they can manage billing.
      </p>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-6 flex items-center justify-center gap-2" role="radiogroup" aria-label="Billing interval">
        {(['monthly', 'annual'] as const).map((value) => (
          <Button
            key={value}
            type="button"
            role="radio"
            aria-checked={interval === value}
            variant={interval === value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setInterval(value)}
          >
            {value === 'monthly' ? 'Monthly' : `Annual — save up to ${MAX_ANNUAL_SAVING_PERCENT}%`}
          </Button>
        ))}
      </div>

      <PromoField promo={promo} />

      <div className="grid gap-4 md:grid-cols-3">
        {tiers.map((tier) => {
          const selected = tier.plan === selectedPlan
          const price = isAnnual ? tier.annualMonthlyPrice : tier.monthlyPrice
          return (
            <div
              key={tier.plan}
              className={`relative flex flex-col rounded-lg border p-5 text-left ${selected ? 'border-primary ring-1 ring-primary' : ''}`}
            >
              {selected && <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">Your pick</Badge>}
              <h3 className="text-lg font-semibold">{tier.name}</h3>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-3xl font-semibold">${formatPrice(price)}</span>
                <span className="text-muted-foreground">/mo</span>
              </div>
              <p className={`text-sm text-muted-foreground ${isAnnual ? 'visible' : 'invisible'}`}>
                billed ${tier.annualTotalPrice}/yr
              </p>
              <ul className="my-4 flex-1 space-y-2">
                {tier.features.filter((f) => f.included).slice(0, 4).map((f) => (
                  <li key={f.name} className="flex items-start gap-2 text-sm">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    {f.name}
                  </li>
                ))}
              </ul>
              <Button
                className="w-full"
                variant={selected ? 'default' : 'outline'}
                disabled={loadingPlan !== null}
                onClick={() => choose(tier.plan)}
              >
                {loadingPlan === tier.plan ? 'Opening checkout...' : `Choose ${tier.name}`}
              </Button>
            </div>
          )
        })}
      </div>

      <p className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
        <ShieldCheck className="h-4 w-4 text-status-up" />
        30-day money-back guarantee. Cancel anytime. Prices exclude tax.
      </p>
    </div>
  )
}
